import React, { useMemo } from 'react';
import { Hash, Calendar, AlertTriangle, ArrowRight, UserCheck, PlayCircle, PauseCircle, CalendarClock, CalendarPlus, MapPin, Users } from 'lucide-react';
import { ReportMetadata, SubstitutionConfig } from '../types';
import { getEffectiveDueDate } from '../services/dateService';
import { contractedQuantityOfRow } from '../services/nepPosition';
import { turnusRowProgress } from '../services/inspectionFigures';
import { getServiceLog } from '../services/serviceJournal';
import { istSifaProjekt } from '../services/projectType';
import { hasDeputy } from '../services/projectRepresentatives';
import { ProjectLock } from '../services/projectLockLogic';
import type { ListProject } from '../services/listProject';
import { ProjectLockHint } from './ProjectLockHint';

interface Props {
    project: ListProject;
    onClick: (project: ListProject) => void;
    /**
     * Eigener Vertretungseintrag des Nutzers, wenn er das Projekt heute
     * vertritt (services/substitutionCard.ts), sonst null. Bis Oktober 2026
     * las die Karte dafuer `isSubstituteView`/`_isSubstitution`, die kein
     * Aufrufer je gesetzt hat — Badge, Farbe und Zeitraum erschienen nie.
     */
    substitution?: SubstitutionConfig | null;
    onPostpone?: (project: ListProject) => void;
    onSkip?: (project: ListProject) => void;
    formatDate: (date?: string | null) => string;
    /** Wer sitzt gerade in diesem Projekt? Fehlt der Wert, entfaellt der Hinweis. */
    lock?: ProjectLock | null;
    currentUserId?: string | null;
    /** Zeitpunkt fuer die Ablaufrechnung der Sperre (aus useProjectLocks). */
    lockNowMs?: number;
}

const ST: Record<string, { l: string; bg: string; tx: string; bar: string; bd: string }> = {
    active:   { l: 'Aktiv',   bg: 'bg-emerald-50', tx: 'text-emerald-700', bar: 'bg-emerald-500', bd: 'border-emerald-200' },
    planning: { l: 'Planung', bg: 'bg-blue-50',    tx: 'text-blue-700',    bar: 'bg-blue-400',    bd: 'border-blue-200' },
    on_hold:  { l: 'Ruht',    bg: 'bg-amber-50',   tx: 'text-amber-700',   bar: 'bg-amber-400',   bd: 'border-amber-200' },
    completed:{ l: 'Beendet', bg: 'bg-purple-50',  tx: 'text-purple-700',  bar: 'bg-purple-400',  bd: 'border-purple-200' },
    finished: { l: 'Archiv',  bg: 'bg-slate-50',   tx: 'text-slate-500',   bar: 'bg-slate-300',   bd: 'border-slate-200' },
};

export const ProjectCard: React.FC<Props> = ({ project, onClick, substitution, onPostpone, onSkip, formatDate, lock, currentUserId, lockNowMs }) => {
    const name = project.name;
    const projectNumber = project.project_number || project.data?.metadata?.projectNumber;
    const coordinator = project.coordinator_name || project.data?.metadata?.coordinator;
    const status = (project.status || project.data?.metadata?.globalData?.status || 'planning') as string;
    const lastDate = project.last_inspection_date || project.data?.metadata?.date;
    const address = project.data?.metadata?.projectAddress || project.address;
    const meta = project.data?.metadata as ReportMetadata | undefined;
    const sc = ST[status] || ST.planning;

    // Beendet und Archiv verhalten sich auf der Karte gleich: keine
    // Faelligkeit mehr, gedaempfte Darstellung.
    const isFinished = status === 'finished' || status === 'completed';
    const isActive = status === 'active';
    const isOnHold = status === 'on_hold';
    const isPlanning = status === 'planning';
    const isSub = !!substitution;
    // SiFa = unbefristete Betreuung ohne Begehungsturnus: keine Faelligkeit,
    // kein "ueberfaellig", kein Verschieben (docs/sifa-betreuung-plan.md).
    const isSifa = istSifaProjekt(meta);
    // "Keine Berichtsfuehrung": ohne Berichte gibt es auch keine letzte
    // Begehung, an der eine Liegezeit zu messen waere.
    const isReportExempt = meta?.globalData?.reportExempt === true;
    // Eine Betreuung laeuft, sie ist nicht "aktiv" im Sinne einer Bauphase.
    const statusLabel = isSifa && isActive ? 'Laufend' : sc.l;

    // Inspection progress: use the service with highest quantity_contracted (turnus position)
    // — nie die Monatspauschale, und im Pauschalprojekt mit den Nachweisen
    // auf der Pauschale: die Turnusposition waechst dort seit der Umstellung
    // nicht mehr (inspectionFigures). Das Protokoll steckt schon in der
    // Listenzeile (metadata), geladen wird nichts.
    const inspProgress = useMemo(
        () => turnusRowProgress(project.project_services, getServiceLog(project.data)),
        [project.project_services, project.data],
    );

    // Value progress fallback
    const valueProgress = useMemo(() => {
        const svcs = project.project_services || [];
        if (!svcs.length) return null;
        let c = 0, u = 0;
        svcs.forEach(s => { const w = Number(s.unit_price) || 1; c += contractedQuantityOfRow(s) * w; u += (Number(s.quantity_used) || 0) * w; });
        return c > 0 ? Math.min(100, Math.round(u / c * 100)) : null;
    }, [project.project_services]);

    // Urgency
    const urgency = useMemo(() => {
        if (isFinished || isOnHold || isPlanning) return { level: 'none' as const, days: 0, label: '' };
        const edd = getEffectiveDueDate(project);
        if (!edd) return { level: 'none' as const, days: 0, label: '' };
        const now = new Date(); now.setHours(0, 0, 0, 0);
        const due = new Date(edd); due.setHours(0, 0, 0, 0);
        const diff = Math.ceil((due.getTime() - now.getTime()) / 864e5);
        if (diff < 0) return { level: 'critical' as const, days: diff, label: `${Math.abs(diff)}d überfällig` };
        if (diff === 0) return { level: 'today' as const, days: 0, label: 'Heute' };
        if (diff <= 3) return { level: 'soon' as const, days: diff, label: `In ${diff}d` };
        return { level: 'normal' as const, days: diff, label: '' };
    }, [project, isFinished, isOnHold, isPlanning]);

    const isLongOverdue = useMemo(
        () => isActive && !isSifa && !isReportExempt && lastDate && (Date.now() - new Date(lastDate).getTime()) / 864e5 > 21,
        [isActive, isSifa, isReportExempt, lastDate],
    );

    const borderClass = (urgency.level === 'critical' || isLongOverdue) ? 'border-red-300 ring-1 ring-red-100'
        : urgency.level === 'today' ? 'border-emerald-300 ring-1 ring-emerald-50'
        : urgency.level === 'soon' ? 'border-amber-200'
        : isSub ? 'border-teal-200' : 'border-slate-200 hover:border-slate-300';

    const topBar = (urgency.level === 'critical' || isLongOverdue) ? 'bg-red-500'
        : urgency.level === 'today' ? 'bg-emerald-500' : isSub ? 'bg-teal-500' : sc.bar;

    const pData = inspProgress || (valueProgress !== null ? { used: valueProgress, contracted: 100, pct: valueProgress } : null);

    const isJoint = hasDeputy(meta);

    return (
        <div
            onClick={() => onClick(project)}
            // Die Karte war ein reines div mit onClick: per Tastatur nicht
            // erreichbar und fuer Screenreader kein Bedienelement.
            role="button"
            tabIndex={0}
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(project); } }}
            aria-label={`Projekt ${name} öffnen`}
            className={`group rounded-xl border glass-panel flex flex-col cursor-pointer relative overflow-hidden hover:bg-white/90 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] ${borderClass} ${isFinished ? 'opacity-50 hover:opacity-70' : ''}`}
        >
            <div className={`h-1 w-full ${topBar}`} />
            <div className="px-4 pt-3 pb-2 flex-1 flex flex-col min-h-0">
                {/* Badges */}
                <div className="flex items-center gap-1 flex-wrap mb-1.5">
                    <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${sc.bg} ${sc.tx} border ${sc.bd}`}>{statusLabel}</span>
                    {isSifa && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200" title="Fachkraft für Arbeitssicherheit — unbefristete Betreuung">SiFa</span>}
                    <ProjectLockHint lock={lock} currentUserId={currentUserId} nowMs={lockNowMs || 0} />
                    {isJoint && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700 border border-indigo-200 flex items-center gap-0.5"><Users className="w-2.5 h-2.5" />Gemeinsam</span>}
                    {isSub && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-teal-50 text-teal-700 border border-teal-200 flex items-center gap-0.5"><UserCheck className="w-2.5 h-2.5" />Vertr.</span>}
                    {(urgency.level === 'critical' || isLongOverdue) && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200 flex items-center gap-0.5"><AlertTriangle className="w-2.5 h-2.5" />{urgency.label || 'Überfällig'}</span>}
                    {urgency.level === 'today' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200">Heute</span>}
                    {urgency.level === 'soon' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-100">{urgency.label}</span>}
                </div>

                {/* Name */}
                <h3 className="font-bold text-[13px] text-slate-800 leading-snug line-clamp-2 group-hover:text-blue-600 transition-colors">{name}</h3>

                {/* Meta */}
                <div className="flex items-center gap-2 mt-1 text-[11px] text-slate-400 overflow-hidden">
                    {projectNumber && <span className="font-mono shrink-0 flex items-center gap-0.5"><Hash className="w-2.5 h-2.5" />{projectNumber}</span>}
                    {address && <span className="truncate flex items-center gap-0.5"><MapPin className="w-2.5 h-2.5 shrink-0" />{address.length > 22 ? address.slice(0, 22) + '…' : address}</span>}
                </div>

                {isSub && coordinator && <div className="text-[11px] text-teal-600 font-bold mt-1">Für: {coordinator}</div>}
                {/* Zeitraum aus dem EIGENEN Eintrag — nicht der erste aktive
                    eines beliebigen Vertreters (zwei gleichzeitige Vertreter). */}
                {substitution && (
                    <div className="text-[10px] text-teal-600 bg-teal-50 px-1.5 py-0.5 rounded mt-0.5 w-fit border border-teal-100 flex items-center gap-1">
                        <Calendar className="w-2.5 h-2.5" />{formatDate(substitution.startDate)} – {formatDate(substitution.endDate)}
                    </div>
                )}

                {/* Progress */}
                {isActive && pData && (
                    <div className="mt-2.5">
                        <div className="flex justify-between text-[11px] mb-0.5">
                            <span className="text-slate-400">{inspProgress ? 'Begehungen' : 'Leistung'}</span>
                            <span className="font-bold text-slate-600">{inspProgress ? `${inspProgress.used}/${inspProgress.contracted}` : `${pData.pct}%`}</span>
                        </div>
                        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden">
                            <div className={`h-full rounded-full transition-all duration-700 ${pData.pct >= 90 ? 'bg-emerald-500' : pData.pct >= 50 ? 'bg-blue-500' : 'bg-amber-400'}`} style={{ width: `${pData.pct}%` }} />
                        </div>
                    </div>
                )}

                {isOnHold && (
                    <div className="mt-2 bg-amber-50 border border-amber-100 p-1.5 rounded-lg flex items-center gap-1.5">
                        <PauseCircle className="w-3 h-3 text-amber-500 shrink-0" />
                        <span className="text-[11px] text-amber-700 font-bold">Pausiert{meta?.globalData?.onHoldUntil ? ` bis ${formatDate(meta.globalData.onHoldUntil)}` : ''}</span>
                    </div>
                )}

                {/* Dates */}
                <div className="mt-auto pt-2 space-y-0.5">
                    {lastDate && <div className="flex justify-between text-[11px]"><span className="text-slate-400 flex items-center gap-1"><Calendar className="w-2.5 h-2.5" />Letzte</span><span className="font-medium text-slate-500">{formatDate(lastDate)}</span></div>}
                    {isActive && !isSifa && <div className="flex justify-between text-[11px]"><span className="text-slate-400 flex items-center gap-1"><CalendarClock className="w-2.5 h-2.5" />Nächste</span><div className="flex items-center gap-1"><span className={`font-bold ${(urgency.level === 'critical' || isLongOverdue) ? 'text-red-600' : urgency.level === 'today' ? 'text-emerald-600' : urgency.level === 'soon' ? 'text-amber-600' : 'text-slate-600'}`}>{formatDate(getEffectiveDueDate(project))}</span>{isJoint && <span className="text-[10px] bg-indigo-50 text-indigo-500 px-1 rounded border border-indigo-100 uppercase">Gemeinsam</span>}</div></div>}
                </div>
            </div>

            {/* Actions */}
            <div className="px-3 py-1.5 border-t border-slate-100 bg-slate-50/30 flex justify-between items-center">
                <button onClick={e => { e.stopPropagation(); onClick(project); }} className="text-[11px] font-bold text-blue-600 flex items-center gap-0.5 hover:text-blue-800"><ArrowRight className="w-3 h-3" /> Öffnen</button>
                {/* Auf Touch-Geraeten gibt es kein Hover — dort waren diese drei
                    Knoepfe dauerhaft unsichtbar. Sie blenden sich erst ab der
                    Desktop-Breite aus, und Tastaturfokus holt sie zurueck. */}
                <div className="flex gap-0.5 transition-opacity duration-200 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100">
                    {onPostpone && isActive && !isSifa && <button onClick={e => { e.stopPropagation(); onPostpone(project); }} className="p-1 hover:bg-amber-50 text-slate-400 hover:text-amber-700 rounded transition-colors" title="+7 Tage" aria-label="Um 7 Tage verschieben"><CalendarPlus className="w-3.5 h-3.5" /></button>}
                    {onSkip && isActive && !isSifa && <button onClick={e => { e.stopPropagation(); onSkip(project); }} className="p-1 hover:bg-blue-50 text-slate-400 hover:text-blue-700 rounded transition-colors" title="Überspringen" aria-label="Begehung überspringen"><CalendarClock className="w-3.5 h-3.5" /></button>}
                    <button onClick={e => { e.stopPropagation(); onClick(project); }} className="p-1 hover:bg-emerald-50 text-slate-400 hover:text-emerald-700 rounded transition-colors" title="Begehung starten" aria-label="Begehung starten"><PlayCircle className="w-3.5 h-3.5" /></button>
                </div>
            </div>
        </div>
    );
};